import { FC, useMemo } from 'react';
import { isPast } from 'date-fns';
import { useDispatch } from 'react-redux';

import { Dialog, DialogClose, DialogContent } from '@app/shared/ui/dialog';
import { useLazyCancelStripeSubscriptionQuery } from '@app/store/api/payments.api';
import { ConfiguredToaster, ToastText, ToastTitle } from '@app/shared/ui/sonner';
import { useTranslation } from '@app/app/i18n';

import { useGetUserInitQuery, userApi } from '@app/store/api/user.api';
import { useAppSelector } from '@app/hooks/use-store';

import { Button } from '@app/shared/ui/button';

import {
  CheckIcon,
  ChevronRightIcon,
  CrossIcon,
  DiamondIcon,
  ErrorCircleIcon,
} from '@app/components/ui/icons';

import { cvaIcon } from '@app/shared/ui/elements';
import { StandardTooltip } from '@app/shared/ui/standard-tooltip';
import { TooltipProvider } from '@app/shared/ui/tooltip';

import { cn } from '@app/app/lib';

import { route_path } from '@app/utils/route_path';

import { LocalizedLink } from '@app/shared/ui/localized-link';

import { usePaymentsDialogStore } from '../model/payments-dialog.store';

import { PaymentsPayg } from './payments-payg';
import { PaymentsPro } from './payments-pro';
import { PaymentsProCancelled } from './payments-pro-cancelled';

import {
  cvaCardContainer,
  cvaHeaderContainer,
  cvaCurrentPlanLabel,
  cvaPlanTitle,
  cvaUpgradeButton,
  cvaContentContainer,
  cvaImageContainer,
  cvaTextContainer,
  cvaContentTitle,
  cvaBackgroundImage,
  cvaCharacterImage,
  cvaContentDescription,
  cvaFeaturesGrid,
  cvaFeatureItem,
  cvaFeatureIcon,
  cvaFeatureText,
} from './payments-dialog-styles';

export const PaymentsDialog: FC = () => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useAppSelector((state) => state.authState);
  const { data: userData } = useGetUserInitQuery(null, { skip: !isAuthenticated });
  const { t } = useTranslation('payments-dialog');

  const [cancelStripeSubscription] = useLazyCancelStripeSubscriptionQuery();

  const { isOpen, setIsOpen } = usePaymentsDialogStore();

  const handleConfirmCancellation = async () => {
    try {
      await cancelStripeSubscription(null).unwrap();

      dispatch(userApi.util.invalidateTags(['INIT_USER']));

      ConfiguredToaster.toastSuccess(
        <>
          <ToastTitle className="mb-1">{t('dialog.toast.cancelledSuccess')}</ToastTitle>
          <ToastText>{t('dialog.toast.cancelledSuccessDescription')}</ToastText>
        </>,
      );
    } catch (error) {
      ConfiguredToaster.toastError(
        <>
          <ToastTitle className="mb-1">{t('dialog.toast.errorCancelling')}</ToastTitle>
          <ToastText>
            {error instanceof Error ? error.message : t('dialog.toast.unknownError')}
          </ToastText>
        </>,
      );
    }
  };

  const totalDownloads = userData?.result?.product_balance?.product?.count ?? 0;
  const remainingDownloads = userData?.result?.product_balance?.count ?? 0;
  const downloadedCount = totalDownloads - remainingDownloads;

  const isSubscription = !!(userData?.result?.product_balance?.type === 'subscription');
  const isPayGPlan = !!(userData?.result?.product_balance?.type === 'presentation');

  const isTrial = !!userData?.result?.product_balance?.product?.is_trial;

  const isSubscriptionActive = !!userData?.result?.subscription_active;

  const productName = userData?.result?.product_balance?.product?.name;

  const expirationDate = userData?.result?.product_balance?.expire;

  const expDateObj = expirationDate ? new Date(expirationDate) : null;
  const hasValidExpDate = expDateObj instanceof Date && !isNaN(expDateObj.getTime());

  const isExpired =
    (isSubscription && hasValidExpDate && isPast(expDateObj)) ||
    (isPayGPlan && remainingDownloads === 0);

  const planName =
    (isExpired && t('dialog.planNames.expired')) ||
    (isTrial && t('dialog.planNames.trial')) ||
    (isSubscription && (productName ?? t('dialog.planNames.pro'))) ||
    t('dialog.planNames.unknown');

  // Вычисляем текущее количество токенов из userData
  const actualTokensCount = useMemo(() => {
    if (!userData?.result?.product_balance) return undefined;
    const { included_ai_token_count = 0, purchased_ai_token_count = 0 } =
      userData.result.product_balance;
    return included_ai_token_count + purchased_ai_token_count;
  }, [userData]);

  const features = [
    t('dialog.features.unlimitedCreate'),
    t('dialog.features.fullAccess'),
    t('dialog.features.unlimitedDownload'),
    t('dialog.features.uploadLogo'),
  ];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => setIsOpen(open)}>
      {isPayGPlan && !isExpired && (
        <PaymentsPayg downloaded={downloadedCount} total={totalDownloads} />
      )}
      {(isExpired || isTrial) && (
        <DialogContent
          className="max-w-[47.5rem]"
          customCloseIcon={
            <DialogClose
              aria-label={t('common.close')}
              className="absolute right-2 top-2 transition-colors disabled:pointer-events-none text-general_text_MII_secondary_i hover:text-general_text_MII_default focus-visible:outline"
            >
              <CrossIcon className={cvaIcon()} />
              <span className="sr-only">{t('common.close')}</span>
            </DialogClose>
          }
        >
          <div className={cvaCardContainer()}>
            {/* Header */}
            <div className={cvaHeaderContainer()}>
              <div className="flex items-center gap-4">
                <div>
                  <p className={cvaCurrentPlanLabel()}>{t('dialog.currentPlan')}</p>
                  <h2 className={cvaPlanTitle()}>{planName}</h2>
                </div>
                {actualTokensCount !== undefined && !isExpired && (
                  <div className="flex items-center gap-2">
                    <div className="inline-flex items-center gap-1.5 py-2 px-3 rounded-3xl bg-general_background_MIII_400_dark">
                      <span className="font-inter text-xs leading-[10px] font-normal text-general_MI_500_default">
                        AI tokens
                      </span>
                      <DiamondIcon className="w-5 h-5 text-general_MI_500_default" />
                      <span className="font-inter text-xs leading-[10px] font-normal text-general_MI_500_default">
                        {actualTokensCount}
                      </span>
                    </div>
                    <TooltipProvider>
                      <StandardTooltip
                        text={t('dialog.currentBalance', {
                          count: actualTokensCount,
                        })}
                        tooltipProps={{ delayDuration: 0 }}
                        contentProps={{ side: 'bottom' }}
                        contentClassName="bg-general_background_MIII_450_dark text-general_text_MII_secondary_i border-0"
                        arrowClassName="hidden"
                      >
                        <div className="cursor-help">
                          <ErrorCircleIcon className={cvaIcon()} />
                        </div>
                      </StandardTooltip>
                    </TooltipProvider>
                  </div>
                )}
              </div>

              <Button
                variant="primary"
                size="semiMedium"
                className={cvaUpgradeButton()}
                onClick={() => setIsOpen(false)}
              >
                <LocalizedLink to={route_path.pricing} className="w-fit">
                  {t('dialog.upgradePlan')}
                </LocalizedLink>
                <ChevronRightIcon className={cvaIcon()} />
              </Button>
            </div>

            {/* Main content */}
            <div className={cvaContentContainer()}>
              {/* Image */}
              <div className={cvaImageContainer()}>
                <img
                  src="/static/i/payments_dialog/confetti.png"
                  alt="Decorative element"
                  role="presentation"
                  aria-hidden="true"
                  className={cvaBackgroundImage()}
                />
                <img
                  src="/static/i/payments_dialog/wonderslide.png"
                  alt="Wunderslide"
                  className={cvaCharacterImage()}
                />
              </div>

              {/* Text content */}
              <div className={cvaTextContainer()}>
                <h3 className={cvaContentTitle()}>{t('dialog.unlockTitle')}</h3>
                <p className={cvaContentDescription()}>{t('dialog.unlockDescription')}</p>

                {/* Features grid */}
                <div className={cvaFeaturesGrid()}>
                  {features.map((feature, index) => (
                    <div key={index} className={cvaFeatureItem()}>
                      <CheckIcon className={cn(cvaIcon({ size: 'small' }), cvaFeatureIcon())} />
                      <span className={cvaFeatureText()}>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      )}

      {isSubscription && !isExpired && !isTrial && (
        <>
          {isSubscriptionActive ? (
            <PaymentsPro onCancel={handleConfirmCancellation} />
          ) : (
            <PaymentsProCancelled />
          )}
        </>
      )}
    </Dialog>
  );
};
